import {
  AddIcon,
  CalendarIcon,
  RepeatIcon,
  SearchIcon,
} from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  FormLabel,
  Heading,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
  Select,
  Text,
  Textarea,
  useToast,
} from "@chakra-ui/react";
import { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  FaEnvelope,
  FaFacebook,
  FaInstagram,
  FaLink,
  FaTwitter,
} from "react-icons/fa";
import { FiEdit } from "react-icons/fi";
import { MdSms } from "react-icons/md";
import { useSelector } from "react-redux";
import { Link as RouterLink } from "react-router-dom";
import { ExportButton } from "../components/ui/exportButton";
import { secureFetch } from "../data/secureFetch";
import { useFileExport } from "../hooks/use-file-export";
import { MAX_EXPORT_SIZE } from "../utils/constants";
import { builQueryString, fiterFilters, objectToString } from "../utils/parser";
import UniversalTable from "./UniversalTable";

const {
  formatCentsToDollars,
  formatDateToTableString,
} = require("../store/calc-helpers");

const defaultPagination = {
  page: 1,
  itemsPerPage: 20,
  totalItems: 0,
  totalPages: 0,
};

const defaultCampaignFilter = {
  search: "",
  status: "",
  minGoal: "",
  maxGoal: "",
  minAmountRaised: "",
  maxAmountRaised: "",
  startDate: "",
  endDate: "",
  page: 1,
  itemsPerPage: 10,
};

function CampaignFilters({
  filters,
  updateFilter,
  handleReset,
  handleExport,
  exportLoading = false,
  onRefresh,
}) {
  return (
    <Box mb={6}>
      <Flex align="center" justify="space-between" flexWrap="wrap" gap={4}>
        {/* SEARCH */}
        <Flex
          align="center"
          justify="space-between"
          p={4}
          borderRadius="lg"
          gap={6}
        >
          <InputGroup width={["100%", "260px"]}>
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.400" />
            </InputLeftElement>

            <Input
              variant="filled"
              placeholder="Search campaings..."
              value={filters.search}
              onChange={(e) => updateFilter("search", e.target.value)}
            />
          </InputGroup>
          {onRefresh && (
            <Button
              leftIcon={<RepeatIcon />}
              colorScheme="blue"
              variant="solid"
              onClick={onRefresh}
              px={4}
              _hover={{ transform: "scale(1.05)" }}
              transition="all 0.2s"
            >
              refresh
            </Button>
          )}
        </Flex>

        {/* RIGHT SECTION */}
        <Flex align="center" gap={3}>
          {/* FILTER POPUP */}
          <Popover placement="bottom-end">
            <PopoverTrigger>
              <Button colorScheme="gray" variant="outline">
                Filters
              </Button>
            </PopoverTrigger>

            <PopoverContent p={4} borderRadius="lg" shadow="xl" width="300px">
              <PopoverArrow />
              <PopoverCloseButton />

              <PopoverBody pt={4}>
                {/* STATUS */}
                <Select
                  placeholder="Status"
                  value={filters.status}
                  onChange={(e) => updateFilter("status", e.target.value)}
                  mb={4}
                >
                  {/* Campaigns: 'active', 'inactive' */}
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </Select>

                {/* GOAL RANGE */}
                <FormLabel fontSize="sm" mb={2} fontWeight="medium">
                  Goal Amount Range
                </FormLabel>
                <Flex gap={3} mb={4}>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">$</InputLeftElement>
                    <Input
                      variant="filled"
                      type="number"
                      placeholder="Min"
                      value={filters.minGoal}
                      onChange={(e) => updateFilter("minGoal", e.target.value)}
                    />
                  </InputGroup>

                  <InputGroup>
                    <InputLeftElement pointerEvents="none">$</InputLeftElement>
                    <Input
                      variant="filled"
                      type="number"
                      placeholder="Max"
                      value={filters.maxGoal}
                      onChange={(e) => updateFilter("maxGoal", e.target.value)}
                    />
                  </InputGroup>
                </Flex>

                {/* AMOUNT RAISED RANGE */}
                <FormLabel fontSize="sm" mb={2} fontWeight="medium">
                  Amount Raised Range
                </FormLabel>
                <Flex gap={3} mb={4}>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">$</InputLeftElement>
                    <Input
                      variant="filled"
                      type="number"
                      placeholder="Min"
                      value={filters.minAmountRaised}
                      onChange={(e) =>
                        updateFilter("minAmountRaised", e.target.value)
                      }
                    />
                  </InputGroup>

                  <InputGroup>
                    <InputLeftElement pointerEvents="none">$</InputLeftElement>
                    <Input
                      variant="filled"
                      type="number"
                      placeholder="Max"
                      value={filters.maxAmountRaised}
                      onChange={(e) =>
                        updateFilter("maxAmountRaised", e.target.value)
                      }
                    />
                  </InputGroup>
                </Flex>

                {/* DATE RANGE */}
                <FormLabel fontSize="sm" mb={2} fontWeight="medium">
                  Start Date Range
                </FormLabel>
                <Flex direction="column" gap={3} mb={4}>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <CalendarIcon color="gray.400" fontSize="sm" />
                    </InputLeftElement>
                    <Input
                      variant="filled"
                      type="date"
                      placeholder="From"
                      value={filters.startDate}
                      onChange={(e) =>
                        updateFilter("startDate", e.target.value)
                      }
                    />
                  </InputGroup>

                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <CalendarIcon color="gray.400" fontSize="sm" />
                    </InputLeftElement>
                    <Input
                      variant="filled"
                      type="date"
                      placeholder="To"
                      value={filters.endDate}
                      onChange={(e) => updateFilter("endDate", e.target.value)}
                    />
                  </InputGroup>
                </Flex>

                {/* RESET */}
                <Button
                  width="100%"
                  mt={2}
                  colorScheme="red"
                  variant="outline"
                  onClick={handleReset}
                >
                  Reset Filters
                </Button>
              </PopoverBody>
            </PopoverContent>
          </Popover>

          {/* EXPORT DROPDOWN */}
          <ExportButton
            exportLoading={exportLoading}
            handleExport={handleExport}
          />
        </Flex>
      </Flex>
    </Box>
  );
}

const ListCampaigns = () => {
  const { exportFile } = useFileExport();

  const { organizationId } = useSelector((state) => state.user);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [pagination, setPagination] = useState(defaultPagination);
  const toast = useToast();
  const [embedCode, setEmbedCode] = useState("");
  const [redirectUrl, setRedirectUrl] = useState("");
  const [filters, setFilters] = useState(defaultCampaignFilter);
  const [exportLoading, setExportLoading] = useState(false);
  const [sortRules, setSortRules] = useState([]);

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const fetchCampaigns = useCallback(async () => {
    if (!organizationId) return;
    try {
      setLoading(true);
      const queryString = builQueryString(filters);
      const response = await secureFetch(
        `/api/campaigns/organization/${organizationId}${queryString}`
      );
      if (response.ok) {
        // Response structure: { data: [...], pagination: {...} }
        // Support both new format (data) and old format (array) for backward compatibility
        let campaignsData = [];
        let paginationData = defaultPagination;

        if (response.data) {
          // New format: { data: [...], pagination: {...} }
          campaignsData = response.data;
          paginationData = response.pagination || defaultPagination;
        } else if (Array.isArray(response)) {
          // Old format: array directly
          campaignsData = response;
          paginationData = {
            page: filters.page || 1,
            itemsPerPage: filters.itemsPerPage || 10,
            totalItems: response.length,
            totalPages: Math.ceil(
              response.length / (filters.itemsPerPage || 10)
            ),
          };
        }

        const formattedData = campaignsData.map((campaign) => {
          return {
            ...campaign,
            goal: campaign.goal
              ? `$${formatCentsToDollars(campaign.goal)}`
              : "-",
            total_fees_saved: `$${formatCentsToDollars(
              campaign.total_fees_saved
            )}`,
            amount_raised: `$${formatCentsToDollars(campaign.amount_raised)}`,
            start_date: campaign.start_date
              ? formatDateToTableString(campaign.start_date)
              : "-",
            end_date: campaign.end_date
              ? formatDateToTableString(campaign.end_date)
              : "-",
          };
        });
        setPagination(paginationData);
        setCampaigns(formattedData);
      } else {
        throw new Error("Failed to fetch campaigns");
      }
    } catch (err) {
      console.log({ err }, "at camapn laodng");
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [organizationId, filters]);

  const handleExport = async (format = "csv") => {
    try {
      setExportLoading(true);

      // Build export filters without pagination limits
      const exportFilters = {
        ...filters,
        page: 1,
        itemsPerPage: MAX_EXPORT_SIZE, // Maximum for export
      };
      const queryString = builQueryString(exportFilters);
      const response = await secureFetch(
        `/api/campaigns/organization/${organizationId}${queryString}`
      );
      if (response.ok) {
        // Response structure: { data: [...], pagination: {...} }
        const campaignsData = response.data || [];
        const formattedData = campaignsData.map((campaign) => {
          return {
            ...campaign,
            goal: campaign.goal
              ? `$${formatCentsToDollars(campaign.goal)}`
              : "-",
            total_fees_saved: `$${formatCentsToDollars(
              campaign.total_fees_saved
            )}`,
            amount_raised: `$${formatCentsToDollars(campaign.amount_raised)}`,
            start_date: campaign.start_date
              ? formatDateToTableString(campaign.start_date)
              : "-",
            end_date: campaign.end_date
              ? formatDateToTableString(campaign.end_date)
              : "-",
          };
        });

        exportFile({
          format,
          data: formattedData,
          columns,
          exportFileName: "Campaigns Records",
          tableName: "Campaigns Records",
          filtersValues: { organizationId },
          sortRules,
        });
      } else {
        throw new Error("Failed to fetch export data");
      }
    } catch (err) {
      setError();
      toast({
        position: "top-right",
        title: `Export failed: ${err.message}`,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setExportLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  const columns = [
    {
      Header: "ID",
      accessor: "id",
    },
    {
      Header: "Name",
      accessor: "name",
    },
    {
      Header: "Goal",
      accessor: "goal",
    },
    {
      Header: "Amount Raised",
      accessor: "amount_raised",
    },
    {
      Header: "Start Date",
      accessor: "start_date",
    },
    {
      Header: "End Date",
      accessor: "end_date",
    },
    {
      Header: "Url",
      accessor: "url",
      Cell: ({ row }) => (
        <Button colorScheme="blue" onClick={() => openShareModal(row.original)}>
          Share
        </Button>
      ),
    },
    {
      Header: "Actions",
      Cell: ({ row }) => (
        <Button
          as={RouterLink}
          to={`/campaigns/edit/${row.original.id}`}
          colorScheme="teal"
          variant="outline"
          leftIcon={<FiEdit />}
        >
          Edit
        </Button>
      ),
    },
  ];

  const normalizeRedirectUrl = (raw) => {
    const v = (raw || "").trim();
    if (!v) return "";
    if (/^https?:\/\//i.test(v)) return v.replace(/^http:\/\//i, "https://");
    return `https://${v}`;
  };

  function generateEmbedCode(campaignId, options = {}) {
    const host = window.location.host || "purelygive.org";
    const params = new URLSearchParams();
    if (options.redirectUrl) {
      params.set("redirect", normalizeRedirectUrl(options.redirectUrl));
    }
    const qs = params.toString();
    const src = `https://${host}/embed/donate/${campaignId}${
      qs ? `?${qs}` : ""
    }`;
    const scriptURL = `https://${host}/scripts/purelygive-embed-loader.v1.js`;
    const iframeId = `purelygive-embed-${campaignId}`;

    return `<iframe\n  id="${iframeId}"\n  src="${src}"\n  title="PurelyGive Donation Form"\n  style="width:100%; height:640px; border:0; display:block; overflow:hidden;"\n></iframe>\n<script async src="${scriptURL}" data-target="#${iframeId}"></script>`;
  }

  const handleGenerateEmbedCode = (campaignId) => {
    const code = generateEmbedCode(campaignId, {
      redirectUrl: redirectUrl?.trim() || "",
    });
    setEmbedCode(code);
    navigator.clipboard.writeText(code);
    toast({
      title: "Embed code generated!",
      description:
        "Custom campaign code has been copied, paste it into your website.",
      status: "success",
      duration: 5000,
      isClosable: true,
    });
  };

  const openShareModal = (campaign) => {
    setSelectedCampaign(campaign);
    setIsModalOpen(true);
  };

  const closeShareModal = () => {
    setIsModalOpen(false);
    setEmbedCode("");
  };

  const selectedCampaignURL = selectedCampaign
    ? `https://${window.location.host}/donate/${selectedCampaign.url}`
    : "";

  const shareMessage = selectedCampaign
    ? `We are raising funds for ${selectedCampaign.name}, and your support will help us reach our goal. Every donation counts.`
    : "";

  const shareMessageWithURL = selectedCampaign
    ? `${shareMessage} Donate today: ${selectedCampaignURL}`
    : "";

  const campaignImage = `https://${window.location.host}/images/share-image.png`;
  return (
    <Box p={5} height={"80vh"}>
      <Helmet>
        <title>Campaigns - PurelyGive</title>
        <meta
          name="description"
          content="Create, manage, and promote your fundraising campaigns with PurelyGive. Reach your goals with zero transaction fees."
        />
        <meta
          name="keywords"
          content="campaigns, create fundraising, manage campaigns, promote causes, PurelyGive"
        />
        <meta
          property="og:title"
          content="Manage Your Campaigns - PurelyGive"
        />
        <meta
          property="og:description"
          content="Launch and oversee your fundraising campaigns effortlessly using PurelyGive's powerful tools."
        />
        <meta property="og:image" content={campaignImage} />
        <meta property="og:url" content="https://purelygive.com/campaigns" />
      </Helmet>

      {/* Header with New Campaign button */}
      <Flex justifyContent="space-between" alignItems="center" mb={6}>
        <Heading>Campaigns</Heading>
        <Flex gap={2}>
          <Button as={RouterLink} to="/fund-groups" variant="outline">
            Manage Fund Groups
          </Button>
          <Button
            as={RouterLink}
            to="/campaigns/create"
            colorScheme="blue"
            leftIcon={<AddIcon />}
          >
            Create New Campaign
          </Button>
        </Flex>
      </Flex>

      <CampaignFilters
        filters={filters}
        updateFilter={updateFilter}
        handleExport={handleExport}
        handleReset={() => {
          setFilters(defaultCampaignFilter);
        }}
        onRefresh={() => {
          setFilters(defaultCampaignFilter);
          fetchCampaigns();
        }}
        exportLoading={exportLoading}
      />

      {/* <UniversalTable columns={columns} data={campaigns}  is/> */}
      <UniversalTable
        columns={columns}
        data={campaigns}
        isLoading={loading}
        error={error}
        showGlobalSearch={false}
        showColumnFilters={false}
        handleExport={handleExport}
        exportLoading={exportLoading}
        tableName="campaings"
        pagination={pagination}
        showPagination
        onPaginationChange={(page, itemsPerPage) => {
          updateFilter("itemsPerPage", itemsPerPage);
          updateFilter("page", page);
        }}
        onRetry={fetchCampaigns}
        isFiltered={
          objectToString(fiterFilters(filters, ["page", "itemsPerPage"])) !== ""
        }
        enableMultiSort={true}
        initialSortBy={sortRules}
        onSortChange={(rules) => {
          setSortRules(rules);
        }}
      />

      {/* Share Modal */}
      <Modal isOpen={isModalOpen} onClose={closeShareModal}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Share this Campaign</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Flex direction="column" mt={6} mb={4}>
              <Text mb={2} fontSize="lg" fontWeight="bold">
                Share this campaign and make a difference!
              </Text>

              <Flex alignItems="center" gap={2}>
                <Input
                  value={selectedCampaignURL}
                  isDisabled
                  readOnly
                  variant="filled"
                  size="md"
                  width="100%"
                />
                <Button
                  leftIcon={<FaLink />}
                  colorScheme="blue"
                  px={6}
                  onClick={() => {
                    navigator.clipboard.writeText(selectedCampaignURL);
                    toast({
                      title: "Link copied!",
                      description:
                        "The campaign link has been copied to your clipboard.",
                      status: "success",
                      duration: 2000,
                      isClosable: true,
                    });
                  }}
                >
                  Copy Link
                </Button>
              </Flex>

              <Flex mt={4} gap={2}>
                <IconButton
                  aria-label="Share on Facebook"
                  icon={<FaFacebook />}
                  colorScheme="facebook"
                  onClick={() => {
                    navigator.clipboard.writeText(shareMessageWithURL);
                    window.open(
                      `https://www.facebook.com/sharer/sharer.php?u=${selectedCampaignURL}`,
                      "_blank"
                    );
                  }}
                />
                <IconButton
                  aria-label="Share on Twitter"
                  icon={<FaTwitter />}
                  colorScheme="twitter"
                  onClick={() => {
                    navigator.clipboard.writeText(shareMessage);
                    window.open(
                      `https://twitter.com/intent/tweet?url=${selectedCampaignURL}&text=${shareMessage}`,
                      "_blank"
                    );
                  }}
                />
                <IconButton
                  aria-label="Share on Instagram"
                  icon={<FaInstagram />}
                  colorScheme="pink"
                  onClick={() => {
                    navigator.clipboard.writeText(shareMessageWithURL);
                    toast({
                      title: "Message copied to clipboard!",
                      description: "You can now paste it on Instagram.",
                      status: "success",
                      duration: 2000,
                      isClosable: true,
                    });
                    const isMobile = /iPhone|iPad|iPod|Android/i.test(
                      navigator.userAgent
                    );
                    if (isMobile) {
                      window.location.href = "instagram://app";
                    } else {
                      window.open("https://www.instagram.com", "_blank");
                    }
                  }}
                />
                <IconButton
                  aria-label="Share via SMS"
                  icon={<MdSms />}
                  colorScheme="teal"
                  onClick={() => {
                    navigator.clipboard.writeText(shareMessageWithURL);
                    window.location.href = `sms:?&body=${shareMessageWithURL}`;
                  }}
                />
                <IconButton
                  aria-label="Share via Email"
                  icon={<FaEnvelope />}
                  colorScheme="gray"
                  onClick={() => {
                    navigator.clipboard.writeText(shareMessageWithURL);
                    window.location.href = `mailto:?subject=Support this campaign&body=${shareMessageWithURL}`;
                  }}
                />
              </Flex>
            </Flex>

            <Box>
              <Button
                onClick={() => handleGenerateEmbedCode(selectedCampaign.id)}
              >
                Generate/Copy Embed Code
              </Button>
              <Box mt={4}>
                <Text mb={1} fontWeight="medium">
                  Optional redirect URL after successful donation
                </Text>
                <Input
                  placeholder="https://your-site.com/thank-you"
                  value={redirectUrl}
                  onChange={(e) => setRedirectUrl(e.target.value)}
                />
                <Text mt={2} fontSize="sm" color="gray.600">
                  If provided, the embedded form will redirect the parent page
                  to this URL after a successful donation.
                </Text>
              </Box>
              {embedCode && (
                <Box mt={4}>
                  <Text>Copy and paste this code into your website:</Text>
                  <Textarea value={embedCode} isReadOnly />
                </Box>
              )}
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={closeShareModal}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ListCampaigns;
